import { useEffect, useRef, useState } from "react";
import api, { inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loading } from "@/components/Loading";
import { ShieldCheck, RefreshCw, LogOut, Store, PhoneCall, Plus, Trash2, BadgeCheck, Ban, SlidersHorizontal } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AdminAds } from "@/components/AdminAds";
import { AdminSiteInfo } from "@/components/AdminSiteInfo";

const AFEATS = [
  { id: "orders", label: "Order Management" },
  { id: "id-certificate", label: "ID / Certificate Creator" },
  { id: "events", label: "Create Event" },
];

const fmtBytes = (b) => {
  const n = Number(b) || 0;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n ? 1 : 0)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
};

export default function AdminPanel({ onExit }) {
  const [key, setKey] = useState(sessionStorage.getItem("ps_admin_key") || "");
  const [shops, setShops] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [approveFor, setApproveFor] = useState(null);
  const [approveForm, setApproveForm] = useState({ days: 30, charge: 0 });
  const qrFileRef = useRef(null);

  const onQrFile = (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const MAX = 500;
        let w = img.width, h = img.height;
        if (Math.max(w, h) > MAX) { const s = MAX / Math.max(w, h); w = Math.round(w * s); h = Math.round(h * s); }
        const c = document.createElement("canvas");
        c.width = w; c.height = h;
        c.getContext("2d").drawImage(img, 0, 0, w, h);
        setSettings((s) => ({ ...s, payment_qr: c.toDataURL("image/png") }));
        toast.success("QR added — Save Settings karna na bhoolein");
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };
  const [approving, setApproving] = useState(false);
  const [featuresFor, setFeaturesFor] = useState(null);
  const [featDisabled, setFeatDisabled] = useState([]);
  const [savingFeat, setSavingFeat] = useState(false);

  const openFeatures = (s) => { setFeaturesFor(s); setFeatDisabled(s.disabled_features || []); };
  const toggleFeat = (id) => setFeatDisabled((d) => d.includes(id) ? d.filter((x) => x !== id) : [...d, id]);
  const saveFeatures = async () => {
    setSavingFeat(true);
    try {
      await api.put(`/admin/shops/${featuresFor.id}/features`, { disabled_features: featDisabled }, { headers: { "X-Admin-Key": key } });
      toast.success(`${featuresFor.name} ke features update ho gaye`);
      setFeaturesFor(null);
      load(key);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSavingFeat(false); }
  };

  const load = async (k) => {
    setBusy(true);
    try {
      const headers = { "X-Admin-Key": k };
      const [r, st] = await Promise.all([
        api.get("/admin/shops", { headers }),
        api.get("/admin/settings", { headers }),
      ]);
      setShops(r.data);
      setSettings(st.data);
      sessionStorage.setItem("ps_admin_key", k);
    } catch (err) {
      sessionStorage.removeItem("ps_admin_key");
      setShops(null);
      setKey("");
      toast.error(err.response?.data?.detail || "Invalid admin key");
    } finally {
      setBusy(false);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const r = await api.put("/admin/settings",
        { admin_phone: settings.admin_phone, plans: settings.plans, payment_qr: settings.payment_qr || "", payment_number: settings.payment_number || "" },
        { headers: { "X-Admin-Key": key } });
      setSettings(r.data);
      toast.success("Settings saved");
    } catch { toast.error("Save failed"); } finally { setSavingSettings(false); }
  };

  const approve = async () => {
    setApproving(true);
    try {
      await api.post(`/admin/shops/${approveFor.id}/approve`,
        { days: Number(approveForm.days) || 0, charge: Number(approveForm.charge) || 0 },
        { headers: { "X-Admin-Key": key } });
      toast.success(`${approveFor.name} approved for ${approveForm.days} days`);
      setApproveFor(null);
      load(key);
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); } finally { setApproving(false); }
  };

  const deactivate = async (s) => {
    if (!window.confirm(`Deactivate "${s.name}"? User ka app access turant band ho jayega.`)) return;
    try {
      await api.post(`/admin/shops/${s.id}/deactivate`, {}, { headers: { "X-Admin-Key": key } });
      toast.success(`${s.name} deactivated`);
      load(key);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deleteShop = async (s) => {
    if (!window.confirm(`DELETE "${s.name}"? Iska SAARA data (orders, customers, ID/Certificate data) permanently delete ho jayega!`)) return;
    try {
      await api.delete(`/admin/shops/${s.id}`, { headers: { "X-Admin-Key": key } });
      toast.success(`${s.name} deleted`);
      load(key);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  useEffect(() => { if (key) load(key); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = () => {
    sessionStorage.removeItem("ps_admin_key");
    setShops(null);
    setKey("");
    setInput("");
    if (onExit) onExit();
  };

  const handleBack = () => {
    if (onExit) {
      onExit();
    } else {
      window.location.href = "/";
    }
  };

  if (shops === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="admin-login">
        <div className="w-full max-w-sm bg-card border border-border rounded-xl shadow-sm p-8">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="h-6 w-6 text-cyan-600" />
            <h1 className="font-heading text-2xl font-bold">Admin Panel</h1>
          </div>
          <p className="text-sm text-muted-foreground mb-6">Enter admin key to continue.</p>
          <form
            onSubmit={(e) => { e.preventDefault(); setKey(input); load(input); }}
            className="space-y-3"
          >
            <Input
              type="password"
              placeholder="Admin Key"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              required
              data-testid="admin-key-input"
            />
            <Button type="submit" disabled={busy} data-testid="admin-login-btn" className="w-full bg-cyan-600 hover:bg-cyan-700 text-white">
              {busy ? "Checking..." : "Open Admin Panel"}
            </Button>
          </form>
          <button
            type="button"
            onClick={handleBack}
            className="block w-full text-center text-xs text-cyan-700 hover:underline mt-4"
          >
            ← Back to App
          </button>
        </div>
      </div>
    );
  }

  const totalSales = shops.reduce((s, x) => s + (x.total_sales || 0), 0);
  const totalDue = shops.reduce((s, x) => s + (x.total_due || 0), 0);

  return (
    <div className="min-h-screen bg-background" data-testid="admin-panel">
      <header className="sticky top-0 z-20 bg-white border-b border-border">
        <div className="h-1 flex">
          <div className="flex-1 bg-cyan-500" />
          <div className="flex-1 bg-pink-600" />
          <div className="flex-1 bg-yellow-400" />
          <div className="flex-1 bg-slate-900" />
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <ShieldCheck className="h-6 w-6 text-cyan-600" />
          <div className="flex-1">
            <p className="font-heading font-bold">Sootrik — Admin Panel</p>
            <p className="text-xs text-muted-foreground">{shops.length} registered shops</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => load(key)} className="gap-1.5" data-testid="admin-refresh-btn">
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
          <Button size="sm" variant="ghost" onClick={logout} data-testid="admin-logout-btn" className="gap-1.5 text-muted-foreground">
            <LogOut className="h-4 w-4" /> Exit
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <AdminAds adminKey={key} />
        <AdminSiteInfo adminKey={key} />
        {settings && (
          <div className="bg-card border border-border rounded-xl p-4 shadow-sm space-y-3" data-testid="admin-settings">
            <div className="flex items-center gap-2">
              <PhoneCall className="h-4 w-4 text-cyan-600" />
              <p className="font-heading font-semibold text-sm">Admin Contact & Plans</p>
            </div>
            <div className="space-y-1 max-w-xs">
              <label className="text-xs text-muted-foreground">Admin Mobile (users will call this number)</label>
              <Input value={settings.admin_phone} onChange={(e) => setSettings({ ...settings, admin_phone: e.target.value })} placeholder="e.g. 9876543210" data-testid="admin-settings-phone" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Payment Number (plan kharidne ke liye)</label>
                <Input value={settings.payment_number || ""} onChange={(e) => setSettings({ ...settings, payment_number: e.target.value })} placeholder="e.g. 9876543210 / UPI ID" data-testid="admin-payment-number" />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Payment QR Code (locked users ko dikhega)</label>
                <div className="flex items-center gap-2">
                  <input type="file" accept="image/*" className="hidden" ref={qrFileRef} onChange={onQrFile} data-testid="admin-qr-file-input" />
                  <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => qrFileRef.current?.click()} data-testid="admin-qr-upload-btn">Upload QR</Button>
                  {settings.payment_qr && (
                    <>
                      <img src={settings.payment_qr} alt="QR" className="h-10 w-10 rounded border object-cover" data-testid="admin-qr-preview" />
                      <button onClick={() => setSettings({ ...settings, payment_qr: "" })} className="text-red-500 hover:text-red-600" data-testid="admin-qr-remove-btn"><Trash2 className="h-4 w-4" /></button>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs text-muted-foreground">Subscription Plans (shown to locked users)</label>
                <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" data-testid="admin-plan-add-btn"
                  onClick={() => setSettings({ ...settings, plans: [...(settings.plans || []), { name: "", days: 30, price: 0 }] })}>
                  <Plus className="h-3 w-3" /> Add Plan
                </Button>
              </div>
              {(settings.plans || []).map((p, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`admin-plan-row-${i}`}>
                  <Input value={p.name} onChange={(e) => setSettings({ ...settings, plans: settings.plans.map((x, j) => j === i ? { ...x, name: e.target.value } : x) })} placeholder="Plan name" className="w-44 h-8 text-xs" data-testid={`admin-plan-name-${i}`} />
                  <Input type="number" min={1} value={p.days} onChange={(e) => setSettings({ ...settings, plans: settings.plans.map((x, j) => j === i ? { ...x, days: parseInt(e.target.value) || 0 } : x) })} placeholder="Days" className="w-20 h-8 text-xs" data-testid={`admin-plan-days-${i}`} />
                  <Input type="number" min={0} value={p.price} onChange={(e) => setSettings({ ...settings, plans: settings.plans.map((x, j) => j === i ? { ...x, price: parseFloat(e.target.value) || 0 } : x) })} placeholder="Price ₹" className="w-24 h-8 text-xs" data-testid={`admin-plan-price-${i}`} />
                  <button onClick={() => setSettings({ ...settings, plans: settings.plans.filter((_, j) => j !== i) })} className="text-red-500 hover:text-red-600" data-testid={`admin-plan-del-${i}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
            <Button size="sm" onClick={saveSettings} disabled={savingSettings} data-testid="admin-settings-save-btn" className="bg-cyan-600 hover:bg-cyan-700 text-white h-8">
              {savingSettings ? "Saving..." : "Save Settings"}
            </Button>
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
          <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
            <p className="text-xs font-medium uppercase tracking-wider text-slate-500">Total Shops</p>
            <p className="font-heading text-2xl font-bold mt-1">{shops.length}</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
            <p className="text-xs font-medium uppercase tracking-wider text-slate-500">Total Orders</p>
            <p className="font-heading text-2xl font-bold mt-1">{shops.reduce((s, x) => s + (x.orders || 0), 0)}</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
            <p className="text-xs font-medium uppercase tracking-wider text-slate-500">Total Sales</p>
            <p className="font-heading text-2xl font-bold mt-1 text-cyan-700">{inr(totalSales)}</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
            <p className="text-xs font-medium uppercase tracking-wider text-slate-500">Total Dues</p>
            <p className="font-heading text-2xl font-bold mt-1 text-red-500">{inr(totalDue)}</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4 shadow-sm" data-testid="admin-total-storage">
            <p className="text-xs font-medium uppercase tracking-wider text-slate-500">Total Storage</p>
            <p className="font-heading text-2xl font-bold mt-1 text-violet-700">{fmtBytes(shops.reduce((s, x) => s + (x.storage_bytes || 0), 0))}</p>
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center gap-2">
            <Store className="h-4 w-4 text-slate-400" />
            <p className="font-heading font-semibold text-sm">Registered Shops</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-shops-table">
              <thead>
                <tr className="bg-slate-50 text-left">
                  <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">User Name</th>
                  <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Owner</th>
                  <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Mobile</th>
                  <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Password</th>
                  <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">City</th>
                  <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Customers</th>
                  <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Orders</th>
                  <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Sales</th>
                  <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Due</th>
                  <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Storage</th>
                  <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Validity</th>
                  <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {shops.map((s) => (
                  <tr key={s.id} className="border-t border-border hover:bg-slate-50/60" data-testid={`admin-shop-row-${s.id.slice(0, 8)}`}>
                    <td className="px-4 py-3 font-medium">{s.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{s.owner_name || "-"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{s.mobile}</td>
                    <td className="px-4 py-3 text-xs" data-testid={`admin-password-${s.id.slice(0, 8)}`}>
                      {s.password ? (
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono font-semibold">{s.password}</span>
                          {s.pw_token && (
                            <Button size="sm" variant="outline" className="h-6 text-[11px] px-2" data-testid={`admin-pwlink-btn-${s.id.slice(0, 8)}`}
                              onClick={() => navigator.clipboard.writeText(`${window.location.origin}/#/pw/${s.pw_token}`).then(() => toast.success("Password link copied — user ko bhej dein"))}>
                              Copy Link
                            </Button>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{s.city || "-"}</td>
                    <td className="px-4 py-3 text-right">{s.customers}</td>
                    <td className="px-4 py-3 text-right">{s.orders}</td>
                    <td className="px-4 py-3 text-right font-semibold">{inr(s.total_sales)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-red-500">{inr(s.total_due)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap" data-testid={`admin-storage-${s.id.slice(0, 8)}`}>
                      <span className="font-semibold text-violet-700">{fmtBytes(s.storage_bytes)}</span>
                      <span className="block text-[11px] text-muted-foreground">{s.storage_files || 0} files</span>
                    </td>
                    <td className="px-4 py-3 text-right text-xs" data-testid={`admin-validity-${s.id.slice(0, 8)}`}>
                      {!s.valid_until ? (
                        <span className="text-slate-500">Lifetime</span>
                      ) : s.active && !s.plan_active ? (
                        <span className="text-amber-600 font-semibold" data-testid={`admin-trial-${s.id.slice(0, 8)}`}>Free Trial · {s.days_left} days left</span>
                      ) : s.active ? (
                        <span className="text-emerald-600 font-semibold">{s.days_left} days left</span>
                      ) : (
                        <span className="text-red-600 font-bold bg-red-50 border border-red-200 rounded-full px-2 py-0.5" data-testid={`admin-inactive-${s.id.slice(0, 8)}`}>Inactive</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button size="sm" variant="outline" className="h-7 text-xs gap-1" data-testid={`admin-approve-btn-${s.id.slice(0, 8)}`}
                          onClick={() => { setApproveFor(s); setApproveForm({ days: 30, charge: 0 }); }}>
                          <BadgeCheck className="h-3.5 w-3.5" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-cyan-700 border-cyan-200 hover:bg-cyan-50" data-testid={`admin-features-btn-${s.id.slice(0, 8)}`}
                          onClick={() => openFeatures(s)}>
                          <SlidersHorizontal className="h-3.5 w-3.5" /> Features
                        </Button>
                        {s.active && (
                          <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-amber-600 border-amber-200 hover:bg-amber-50" data-testid={`admin-deactivate-btn-${s.id.slice(0, 8)}`}
                            onClick={() => deactivate(s)}>
                            <Ban className="h-3.5 w-3.5" /> Deactivate
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-red-500 border-red-200 hover:bg-red-50" data-testid={`admin-delete-btn-${s.id.slice(0, 8)}`}
                          onClick={() => deleteShop(s)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {shops.length === 0 && (
                  <tr><td colSpan="12" className="px-4 py-8 text-center text-muted-foreground text-sm">No shops registered yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <Dialog open={!!approveFor} onOpenChange={(o) => !o && setApproveFor(null)}>
        <DialogContent data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle>Approve — {approveFor?.name}</DialogTitle>
            <DialogDescription>Activation aaj se itne din ke liye hogi. Agar abhi valid hai to usme days add ho jayenge. Charge sirf record ke liye hai.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Days</label>
              <Input type="number" min={1} value={approveForm.days} onChange={(e) => setApproveForm({ ...approveForm, days: e.target.value })} data-testid="approve-days-input" />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Charge (₹)</label>
              <Input type="number" min={0} value={approveForm.charge} onChange={(e) => setApproveForm({ ...approveForm, charge: e.target.value })} data-testid="approve-charge-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveFor(null)}>Cancel</Button>
            <Button onClick={approve} disabled={approving} data-testid="approve-submit-btn" className="bg-cyan-600 hover:bg-cyan-700 text-white">
              {approving ? "Approving..." : "Approve"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!featuresFor} onOpenChange={(o) => !o && setFeaturesFor(null)}>
        <DialogContent data-testid="features-dialog">
          <DialogHeader>
            <DialogTitle>Features — {featuresFor?.name}</DialogTitle>
            <DialogDescription>Jis feature ko OFF karoge, wo user apne app me use nahi kar payega. Dobara ON karne par turant mil jayega.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2.5">
            {AFEATS.map((f) => {
              const on = !featDisabled.includes(f.id);
              return (
                <label key={f.id} className="flex items-center justify-between gap-3 border border-border rounded-lg px-3 py-2.5 cursor-pointer" data-testid={`features-row-${f.id}`}>
                  <span className="text-sm font-medium">{f.label}</span>
                  <span className="flex items-center gap-2 text-xs font-semibold">
                    <span className={on ? "text-emerald-600" : "text-red-500"}>{on ? "ON" : "OFF"}</span>
                    <input type="checkbox" checked={on} onChange={() => toggleFeat(f.id)} data-testid={`features-toggle-${f.id}`} className="h-5 w-5 accent-cyan-600" />
                  </span>
                </label>
              );
            })}
            <p className="text-xs text-muted-foreground">Tick = ON (user use kar sakta hai), Untick = OFF (band ho jayega).</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFeaturesFor(null)}>Cancel</Button>
            <Button onClick={saveFeatures} disabled={savingFeat} data-testid="features-save-btn" className="bg-cyan-600 hover:bg-cyan-700 text-white">
              {savingFeat ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
